/**
 * Keepers › Farm — Active Farm Rosters, and every turn ever taken.
 *
 * Built from `handoff/FFL Pass 03 Farm.dc.html` (1a is the phone, 1b the 1400px desktop) against
 * `handoff/farm-brief.md` and `handoff/chip-system.md`. What it replaces was eleven screens and
 * 9,004px, most of it explanation of how farm turns work. That explanation now lives in the
 * rulebook and this screen carries one `§` citation to it — see the TODO on `RULE_ANCHOR`.
 *
 * FOUR THINGS THIS FILE IS NOT ALLOWED TO DO, each a documented past failure (`farm-brief.md` §0):
 *
 *   NO LOCAL CHIPS. Position, team and outcome all come from `./chips.js`. There is no hand-written
 *   hex anywhere below and no `<span>` pretending to be a chip. `tests/farmChrome.test.ts` fails on
 *   a colour literal in this file.
 *
 *   NO DRIFTED VALUES. Every number here is the design's, including the seven-column desktop grid,
 *   which is `64px 74px 1fr 54px 146px 132px 138px` in `styles.css` and nowhere else.
 *
 *   NO SHORTENED SCHOOLS. Chips never shrink; the PLAYER NAME truncates. That is why the name has
 *   `min-width:0` around it and the chips have `flex-shrink:0`.
 *
 *   NO SILENT GAPS. A school with no chip throws in dev rather than rendering grey.
 */

import { useEffect, useMemo, useState } from 'react';
import { currentManagers, managerShort } from '../domain/identity.js';
import {
  FARM_CAP, PICKS, TURNS, farmItems, farmRosters, farmStats, slotsUsed,
  type Item, type Turn,
} from '../domain/farmScreen.js';
import { CollegeChip, OutcomeChip, PosChip } from './chips.js';
import { Rule } from './RuleText.js';
import { useWindowedList } from './useWindowedList.js';

/**
 * The one rule citation this screen carries — and it is LIVE, not the dead TODO the brief §7 asked
 * for. §7 assumed the destination did not exist; it does. `src/data/rulebook.json` carries the
 * whole farm chain (2013-III-1, 2015-III-2, 2015-III-5, 2017-II-5, 2018-II-1, 2019-II-6,
 * 2022-farm-system-keepers and the rest), and `RuleText.tsx` already renders a `§` that opens the
 * panel on them. `2022-farm-system` — the id the handoff guessed at — is the one thing that is NOT
 * in there.
 *
 * It points at 2013-III-1, "Farm System Designation": the rule that CREATES the farm system, still
 * active, and the head of the amendment chain. The panel derives related and amending rules from
 * `supersedes`/`supersededBy`/`topics`, so opening it reaches every later amendment without this
 * screen listing them — which is exactly why the explanation could be taken off the page.
 */
const RULE_ID = '2013-III-1';

/** Below this the two phone-only filters appear. `farm-brief.md` §4, pinned by §6. */
const PHONE_MAX = 900;

function useIsPhone(): boolean {
  const query = `(max-width: ${PHONE_MAX - 1}px)`;
  const [phone, setPhone] = useState(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && window.matchMedia(query).matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(query);
    const on = () => setPhone(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return phone;
}



/* -- Active Farm Rosters --------------------------------------------------------------------- */

function HeldPlayer({ t }: { t: Turn }) {
  return (
    <span className="fmheld">
      <b>{t.player}</b>
      {/*
        `inline-flex` with a 5px gap and a 1px optical lift, so the pair of chips sits on the name's
        baseline rather than hanging below it. Same construction on both breakpoints.
      */}
      <span className="fmchips">
        {t.pos && <PosChip pos={t.pos} />}
        {t.school && <CollegeChip school={t.school} />}
      </span>
    </span>
  );
}

function Rosters() {
  const rosters = useMemo(() => farmRosters(currentManagers().map((m) => m.id)), []);
  const { used, total } = slotsUsed(rosters);

  return (
    <section className="fmcard">
      <header className="fmcardhd">
        <span className="fmcaps">In college now</span>
        <span className="fmmut">{used} of {total} slots</span>
      </header>
      {rosters.map((r) => (
        <div className={`fmgm${r.held.length > 1 ? ' multi' : ''}`} key={r.gm}>
          <span className="fmgmname">{managerShort(r.gm)}</span>
          <span className="fmgmcount">{r.held.length}/{FARM_CAP}</span>
          {r.held.length === 0
            ? <span className="fmnone">nobody in college right now</span>
            : (
              <div className="fmgmheld">
                {r.held.map((t) => (
                  <div className="fmgmline" key={t.key}>
                    <HeldPlayer t={t} />
                    <span className="fmbid">₣{t.bid ?? 0}</span>
                  </div>
                ))}
              </div>
            )}
        </div>
      ))}
    </section>
  );
}

/* -- the four stat cards ---------------------------------------------------------------------- */

/** Four, not five. No sixth "insight" tile, no asterisk under them. */
function Stats() {
  const s = useMemo(() => farmStats(PICKS), []);
  const cards: Array<[number, string]> = [
    [s.acquired, 'players acquired'],
    [s.reachedNfl, 'reached the NFL'],
    [s.passed, 'turns passed'],
    [s.inCollege, 'still in college'],
  ];
  return (
    <div className="fmstats">
      {cards.map(([n, label]) => (
        <div className="fmstat" key={label}><b>{n}</b><span>{label}</span></div>
      ))}
    </div>
  );
}

/* -- rows ------------------------------------------------------------------------------------- */

/**
 * A passed turn. Half height, no chips, no outcome — nine passes in a twenty-turn season cost
 * 306px this way instead of 612px, which is most of why the log fits on a phone at all.
 */
function PassRow({ t }: { t: Turn }) {
  return (
    <li className="fmpassrow">
      <span className="fmorder">{t.order}</span>
      <span className="fmpassed">{t.nominator} passed</span>
    </li>
  );
}

function PhoneRow({ t }: { t: Turn }) {
  const [surname, ...rest] = t.player.split(' ');
  const given = rest.join(' ');
  return (
    <li className="fmrow">
      <span className="fmslot">
        <b>{t.order}</b>
        <i>{t.nominator}</i>
      </span>
      <span className="fmbody">
        {/*
          Surname first and never wrapped: a hundred and forty rows are scanned down the surnames,
          and a name that folds breaks the column the eye is running down. The GIVEN name is what
          gives way — never the school chip.
        */}
        <span className="fmname">
          <b>{given ? `${surname},` : surname}</b>
          {given && <i>{given}</i>}
          {t.pos && <PosChip pos={t.pos} />}
          {t.school && <CollegeChip school={t.school} />}
        </span>
        <span className="fmmeta">
          <span className="fmwon">{t.wonBy}</span>
          {t.bid !== null && <span className="fmbid">₣{t.bid}</span>}
          {t.outcome && (
            <OutcomeChip label={t.outcome.short} tone={t.outcome.tone}
              detail={t.outcome.detail} ariaLabel={t.outcome.aria} />
          )}
        </span>
      </span>
    </li>
  );
}

function DeskRow({ t }: { t: Turn }) {
  return (
    <li className="fmtr">
      <span className="fmorder">{t.order}</span>
      <span className="fmwon">{t.wonBy}</span>
      <span className="fmplayer">{t.player}{t.bid !== null && <i>₣{t.bid}</i>}</span>
      <span>{t.pos && <PosChip pos={t.pos} />}</span>
      <span className="fmschool">{t.school && <CollegeChip school={t.school} />}</span>
      <span>
        {t.outcome && (
          <OutcomeChip label={t.outcome.long} tone={t.outcome.tone}
            detail={t.outcome.detail} ariaLabel={t.outcome.aria} />
        )}
      </span>
      <span className={t.leagueDraft ? 'fmlg' : 'fmlg none'}>{t.leagueDraft || '—'}</span>
    </li>
  );
}

/* -- the list ---------------------------------------------------------------------------------- */

const heightVar = (i: Item) =>
  i.kind === 'band' ? '--fm-band-h' : i.turn.passed ? '--fm-pass-h' : '--fm-turn-h';
const isBand = (i: Item) => i.kind === 'band';

function History({ phone }: { phone: boolean }) {
  const [q, setQ] = useState('');
  const [gm, setGm] = useState('');
  const [status, setStatus] = useState('');

  const gms = useMemo(
    () => [...new Set(TURNS.flatMap((t) => [t.wonBy, t.nominator]))]
      .filter((g) => g && g !== '--').sort(),
    [],
  );

  /*
   * The two extra filters are IGNORED when the layout leaves the phone, not merely hidden. On
   * desktop the table's own columns do that work and the controls are not rendered — but a GM
   * still set from a narrow window would go on filtering a list with no visible control to undo
   * it, which is the worst of both.
   */
  const items = useMemo(
    () => farmItems(phone ? { q, gm, status } : { q }),
    [phone, q, gm, status],
  );

  const win = useWindowedList({ items, heightVar, isAnchor: isBand });

  return (
    <section className="fmhistory">
      <header className="fmhistoryhd">
        <h3>Farm History</h3>
        {phone && <Stats />}
        <div className="fmfilters">
          <input className="fmsearch" type="search" value={q} placeholder="Search by name or school"
            aria-label="Search farm history by name or school"
            onChange={(e) => setQ(e.target.value)} />
          {/*
            GM and Status are PHONE ONLY — absent above 900px, not hidden. `farm-brief.md` §4, and
            §6 checks for their absence rather than for their display value.
          */}
          {phone && (
            <>
              <select className="fmpill" value={gm} aria-label="Filter by GM"
                onChange={(e) => setGm(e.target.value)}>
                <option value="">All GMs</option>
                {gms.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
              <select className="fmpill" value={status} aria-label="Filter by status"
                onChange={(e) => setStatus(e.target.value)}>
                <option value="">All status</option>
                <option value="nfl">Reached the NFL</option>
                <option value="college">In college</option>
                <option value="ended">Rights ended</option>
                <option value="passed">Passed</option>
              </select>
            </>
          )}
        </div>
      </header>

      {!phone && (
        <div className="fmthead" role="presentation">
          <span>Order</span><span>Won by</span><span>Player</span><span>Pos</span>
          <span>School</span><span>NFL outcome</span><span>League draft</span>
        </div>
      )}

      {/*
        ONE scroll container, all 141 turns, newest first. Not accordions, not pages, not a list per
        season — the point is being able to scroll from 2025 to 2013 without knowing which years to
        open first. The bands stick because the windowing keeps its rows in normal flow; see
        `useWindowedList`.
      */}
      <ol className="fmlist" ref={win.ref}>
        <li className="fmpad" style={{ height: win.topPad }} aria-hidden="true" />
        {items.slice(win.start, win.end).map((it) => (
          it.kind === 'band'
            ? (
              <li className="fmband" key={it.key}>
                <b>{it.band.season}</b>
                <span>{phone ? it.band.short : it.band.long}</span>
              </li>
            )
            : it.turn.passed ? <PassRow t={it.turn} key={it.key} />
              : phone ? <PhoneRow t={it.turn} key={it.key} />
                : <DeskRow t={it.turn} key={it.key} />
        ))}
        <li className="fmpad" style={{ height: win.bottomPad }} aria-hidden="true" />
        {items.length === 0 && <li className="fmempty">No turns match that.</li>}
      </ol>
    </section>
  );
}

/* -- the screen -------------------------------------------------------------------------------- */

export function FarmLog() {
  const phone = useIsPhone();
  return (
    <div className="fmpage">
      <div className="fmhead">
        <h2>Active Farm Rosters</h2>
        <Rule id={RULE_ID} />
      </div>
      <div className="fmgrid">
        <div className="fmleft">
          {/* 2×2 above the roster card on desktop; on the phone they sit above Farm History. */}
          {!phone && <Stats />}
          <Rosters />
        </div>
        <History phone={phone} />
      </div>
    </div>
  );
}
